import { JSX } from 'react';
import {
    CreateButton,
    Datagrid,
    DeleteButton,
    EditButton,
    FunctionField,
    List,
    TextField,
    TopToolbar,
    usePermissions,
} from 'react-admin';
import { Chip } from '@mui/material';
import { AdminRole } from '../../types/entities';
import { permissionManager } from '../permissions/manager';

/**
 * The ACL roles.
 *
 * A system role shows no Edit or Delete button, and a role somebody still holds shows no Delete.
 * HQ-API refuses both regardless, through `AdminRoleWriteSubscriber`; hiding the button is so an
 * admin is not offered an action that can only fail.
 *
 * The role the signed-in admin holds is marked and cannot be edited by them, which is Magento's
 * rule and the thing that stops the screen being a way to award yourself anything one grant at a
 * time.
 */

const ListActions: () => JSX.Element = (): JSX.Element => (
    <TopToolbar>
        <CreateButton />
    </TopToolbar>
);

export const AdminRoleList: () => JSX.Element = (): JSX.Element => {
    const { permissions } = usePermissions();
    const heldRole: string | null = permissionManager.roleName(permissions);

    return (
        <List actions={ <ListActions /> } exporter={ false }>
            <Datagrid bulkActionButtons={ false }>
                <TextField source="name" label="Role" />
                <TextField source="description" label="Description" />
                <FunctionField
                    label="Grants"
                    render={ (record: AdminRole): string =>
                        record.isAll
                            ? 'Everything'
                            : `${ record.resources.length } resource${ record.resources.length === 1 ? '' : 's' }` }
                />
                <FunctionField label="Admins" render={ (record: AdminRole): string => String(record.userCount) } />
                <FunctionField
                    label=""
                    render={ (record: AdminRole): JSX.Element | null => {
                        if (record.isSystem) {
                            return <Chip size="small" label="System" />;
                        }
                        if (heldRole !== null && record.name === heldRole) {
                            return <Chip size="small" label="Yours" variant="outlined" />;
                        }

                        return null;
                    } }
                />
                <FunctionField
                    label=""
                    render={ (record: AdminRole): JSX.Element | null =>
                        record.isSystem || (heldRole !== null && record.name === heldRole)
                            ? null
                            : <EditButton /> }
                />
                <FunctionField
                    label=""
                    render={ (record: AdminRole): JSX.Element | null =>
                        record.isSystem || record.userCount > 0 || (heldRole !== null && record.name === heldRole)
                            ? null
                            : <DeleteButton mutationMode="pessimistic" /> }
                />
            </Datagrid>
        </List>
    );
}
